import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, CreditCard, Gift, Loader2, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteTheme } from '../hooks/useSiteTheme';
import { usePageScroll } from '../hooks/usePageScroll';
import { afterSignInPath } from '../lib/authApi';
import { getBillingConfig, startCheckout, openPortal, syncBilling, applyReferral, pendingReferralCode, forgetReferralCode, cleanReferralCode, rememberAfterSubscribe, subscriptionSummary } from '../lib/billingApi';
import { apiErrorMessage } from '../lib/projectApi';
import { SiteHeader } from '../components/billing/SiteHeader';
import { SiteFooter } from '../components/legal/SiteFooter';
import { PlanCard } from '../components/billing/PlanCard';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

/**
 * The paywall. WannaPublish is paid-only: a signed-in account without a trialing / active subscription lands here
 * (RequireSubscription in App.js) and picks Monthly or Annual — Stripe Checkout collects the card and starts the
 * 14-day trial (one per account); Stripe sends the browser back to /subscribe/success. Someone who arrived through a
 * friend's link sees their perk on the cards; anyone else can type a referral code before checking out.
 */
export default function Subscribe() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, toggleTheme] = useSiteTheme();
  usePageScroll(); // index.css hides body overflow for the editor; this page scrolls like a normal web page
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [busy, setBusy] = useState(''); // plan id while Checkout opens | 'portal' | 'sync'
  const [code, setCode] = useState(() => pendingReferralCode());
  const [codeError, setCodeError] = useState('');
  const [applying, setApplying] = useState(false);

  const nextPath = afterSignInPath(location.search); // /app, keeping a share link's ?share=…

  const loadConfig = async () => {
    setConfigError('');
    try { setConfig(await getBillingConfig()); } catch (e) { setConfigError(await apiErrorMessage(e, 'The plans could not be loaded.')); }
  };
  useEffect(() => { loadConfig(); }, []);

  // back from a Checkout the user closed
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('canceled') !== '1') return;
    toast('Checkout closed', { description: 'Nothing was charged. Pick a plan whenever you are ready.', id: 'checkout' });
    params.delete('canceled');
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
    // once on arrival: ?canceled=1 is consumed and removed from the URL; depending on location would re-run on that rewrite
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;
  const billing = user.billing || null;
  const referred = !!user.referral?.referred_by;
  const trial = billing?.trial_eligible !== false;
  const summary = subscriptionSummary(billing);
  const hasAccess = user.editor_access !== false;
  const hadPlan = ['canceled', 'unpaid', 'incomplete_expired', 'incomplete'].includes(billing?.status);

  const start = async (planId) => {
    if (busy) return;
    setBusy(planId);
    try {
      rememberAfterSubscribe(nextPath);
      const { url } = await startCheckout({ plan: planId, referralCode: referred ? null : cleanReferralCode(code) || null });
      forgetReferralCode();
      window.location.assign(url);
    } catch (e) {
      setBusy('');
      const status = e?.response?.status;
      if (status === 409) { await refresh(); toast.success('You already have a subscription', { description: 'Opening the editor…' }); navigate(nextPath, { replace: true }); return; }
      toast.error(await apiErrorMessage(e, 'The checkout could not be started.'));
    }
  };

  const apply = async () => {
    const c = cleanReferralCode(code);
    if (!c || applying) return;
    setApplying(true);
    setCodeError('');
    try {
      const res = await applyReferral(c);
      forgetReferralCode();
      await refresh();
      toast.success(`Invitation from ${res.referrer_first_name || 'a friend'} applied`, { description: 'Your discount is shown on the plans below.' });
    } catch (e) {
      setCodeError(await apiErrorMessage(e, 'That code could not be applied.'));
    } finally { setApplying(false); }
  };

  const portal = async () => {
    if (busy) return;
    setBusy('portal');
    try { const { url } = await openPortal(); window.location.assign(url); } catch (e) { setBusy(''); toast.error(await apiErrorMessage(e, 'The billing portal could not be opened.')); }
  };

  const sync = async () => {
    if (busy) return;
    setBusy('sync');
    try {
      const res = await syncBilling();
      await refresh();
      if (res.allows) { toast.success('Your subscription is active'); navigate(nextPath, { replace: true }); } else toast('No active subscription found', { description: 'Pick a plan below to continue.' });
    } catch (e) { toast.error(await apiErrorMessage(e, 'Stripe could not be reached.')); } finally { setBusy(''); }
  };

  const plans = config?.plans || [];

  return (
    <main className="min-h-[100dvh] flex flex-col bg-background text-foreground" data-testid="subscribe-page" data-theme={theme}>
      <SiteHeader theme={theme} onToggleTheme={toggleTheme} testId="subscribe-header" />

      <section className="flex-1 w-full max-w-[1040px] mx-auto px-4 sm:px-6 pb-10 pt-4 sm:pt-8" aria-labelledby="subscribe-title">
        {config && !config.enabled && (
          <Alert className="mb-6 border-border bg-muted/40" data-testid="subscribe-billing-disabled">
            <AlertDescription className="text-sm flex items-start gap-2"><TriangleAlert className="h-4 w-4 shrink-0 text-[hsl(var(--wp-context-amber))] mt-0.5" aria-hidden="true" /><span>Billing is not set up on this server, so the editor is open to every account for now.</span></AlertDescription>
          </Alert>
        )}

        {hasAccess ? (
          <div className="max-w-[560px] mx-auto rounded-xl border border-border bg-card p-6 shadow-sm" data-testid="subscribe-already-active">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-6 w-6 shrink-0 text-primary mt-0.5" aria-hidden="true" />
              <div className="min-w-0">
                <h1 id="subscribe-title" className="text-xl font-semibold tracking-tight" style={heading}>You are all set</h1>
                <p className="mt-1 text-sm text-foreground" data-testid="subscribe-active-summary">{summary.title}</p>
                <p className="text-xs text-muted-foreground">{summary.detail}</p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button type="button" className="h-10" onClick={() => navigate(nextPath, { replace: true })} data-testid="subscribe-open-editor">Open the editor<ArrowRight className="h-4 w-4 ml-1.5" aria-hidden="true" /></Button>
              {billing && <Button type="button" variant="outline" className="h-10" onClick={portal} disabled={!!busy} data-testid="subscribe-manage-billing"><CreditCard className="h-4 w-4 mr-1.5" aria-hidden="true" />{busy === 'portal' ? 'Opening…' : 'Billing & plan'}</Button>}
            </div>
          </div>
        ) : (
          <>
            <div className="max-w-[640px]">
              <p className="text-xs font-medium uppercase tracking-wider text-primary" data-testid="subscribe-kicker">{hadPlan ? 'Welcome back' : trial ? `${config?.trial_days || 14}-day free trial` : 'Subscribe'}</p>
              <h1 id="subscribe-title" className="mt-1.5 text-3xl sm:text-4xl font-semibold tracking-tight" style={heading} data-testid="subscribe-title">
                {hadPlan ? 'Pick a plan to continue where you left off' : trial ? 'Try everything free for 14 days' : 'Choose your plan'}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-muted-foreground" data-testid="subscribe-lede">
                {trial
                  ? 'Every template, tool and export — nothing held back. A card is needed to start the trial, but it is not charged until the trial ends, and you can cancel in two clicks before then.'
                  : 'Every template, tool and export — nothing held back. Switch plans, update your card or cancel any time from Billing & plan.'}
              </p>
            </div>

            {hadPlan && (
              <Alert className="mt-6 border-border bg-muted/40" data-testid="subscribe-ended-notice">
                <AlertDescription className="text-sm flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="flex items-start gap-2"><TriangleAlert className="h-4 w-4 shrink-0 text-[hsl(var(--wp-context-amber))] mt-0.5" aria-hidden="true" /><span><span className="font-medium text-foreground">{summary.title}.</span> {summary.detail} Your publications are exactly as you left them.</span></span>
                  <span className="flex gap-2 ml-auto">
                    <Button type="button" variant="outline" size="sm" className="h-8" onClick={sync} disabled={!!busy} data-testid="subscribe-refresh-status"><RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy === 'sync' ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh status</Button>
                    <Button type="button" variant="outline" size="sm" className="h-8" onClick={portal} disabled={!!busy} data-testid="subscribe-portal-button"><CreditCard className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Invoices & card</Button>
                  </span>
                </AlertDescription>
              </Alert>
            )}

            {referred && (
              <Alert className="mt-6 border-primary/30 bg-primary/5" data-testid="subscribe-referred-notice">
                <AlertDescription className="text-sm flex items-start gap-2"><Gift className="h-4 w-4 shrink-0 text-primary mt-0.5" aria-hidden="true" /><span>A friend invited you — <span className="font-medium text-foreground">your first month is free</span> on the monthly plan or <span className="font-medium text-foreground">$5 off</span> the annual one. The discount is applied automatically at checkout.</span></AlertDescription>
              </Alert>
            )}

            <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="plan-grid">
              {configError ? (
                <div className="md:col-span-2 rounded-xl border border-border bg-card p-6 text-sm flex flex-wrap items-center gap-3" data-testid="subscribe-config-error">
                  <TriangleAlert className="h-5 w-5 text-destructive" aria-hidden="true" /><span className="flex-1">{configError}</span>
                  <Button type="button" variant="outline" size="sm" onClick={loadConfig} data-testid="subscribe-config-retry">Try again</Button>
                </div>
              ) : !config ? (
                [0, 1].map((i) => <Skeleton key={i} className="h-[420px] rounded-xl" data-testid="plan-skeleton" />)
              ) : (
                plans.map((p) => <PlanCard key={p.id} plan={p} featured={p.id === 'annual'} referred={referred} trial={trial} trialDays={config.trial_days} busy={busy === p.id} disabled={!!busy || !config.enabled} onStart={start} />)
              )}
            </div>

            {!referred && config?.enabled && (
              <div className="mt-6 rounded-xl border border-border bg-card p-4 sm:p-5" data-testid="referral-code-box">
                <div className="flex items-start gap-3">
                  <Gift className="h-5 w-5 shrink-0 text-primary mt-0.5" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="referral-code" className="text-sm font-semibold">Invited by a friend?</Label>
                    <p className="mt-0.5 text-xs text-muted-foreground">Enter their code and your first month is free (or $5 off the annual plan). Your friend is thanked too, once your first bill is paid.</p>
                    <form className="mt-3 flex flex-col sm:flex-row gap-2" onSubmit={(e) => { e.preventDefault(); apply(); }}>
                      <Input id="referral-code" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeError(''); }} placeholder="Friend's code" maxLength={16} autoComplete="off" spellCheck={false} className="h-10 font-mono tracking-wider uppercase sm:max-w-[220px]" aria-invalid={!!codeError || undefined} data-testid="referral-code-input" />
                      <Button type="submit" variant="outline" className="h-10" disabled={applying || !cleanReferralCode(code)} data-testid="referral-code-apply">{applying ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : 'Apply code'}</Button>
                    </form>
                    {codeError && <p role="alert" className="mt-2 text-xs text-destructive" data-testid="referral-code-error">{codeError}</p>}
                  </div>
                </div>
              </div>
            )}

            <p className="mt-8 text-[12px] text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="subscribe-footnote">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden="true" />Secure checkout by Stripe — WannaPublish never sees your card number.</span>
              <span>Prices in USD. Switch between monthly and annual, update your card or cancel any time from <span className="text-foreground">Billing &amp; plan</span>.</span>
            </p>
            <p className="mt-2 text-[12px] text-muted-foreground" data-testid="subscribe-consent">
              By starting a plan you agree to the <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">Terms of Service</Link> — including automatic renewal, how to cancel and the refund policy — and the <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>.
            </p>
          </>
        )}
      </section>
      <SiteFooter testId="subscribe-footer" />
    </main>
  );
}
